import axios, { AxiosRequestConfig } from "axios"
import Axios, { AxiosResponse } from "axios"
import qs from "qs"
import { useRouter } from "next/router"
import { Credential } from "~/model/Authorization"
import { User } from "~/model/User"
import { clear, selectAuthorization, setUser, selectActor } from "~/slices/authorizationSlice"
import { useDispatch, useSelector } from "react-redux"
import { fetchConfig } from "~/slices/configSlice"
import { useGlobal } from "~/composable/useGlobal"
import { useEffect } from "react"
import API from "./api"
import { WorkflowActor } from "~/model/Workflow"
import { CompanyType } from "~/model/Company"

export type HeaderResponse = {
  code: string | null
  errorMessage: string | null
  timestamp: number
  traceId: string
  timeElapsed: string
  memoryPeak: string
}

type Response<T> = {
  data: T
  header: HeaderResponse
}

export type Params = {
  [KEY in string]: any
}

axios.defaults.baseURL = process.env.NEXT_PUBLIC_API_URL
const objectGlob = {
  comanyType: CompanyType.MANAGEMENT,
  handleLogout: (uri: string) => null,
  handleMaintenance: () => null,
  oldUri: ""
};

axios.interceptors.response.use(
  function (response) {
    return response
  },
  function (error) {
    if (error.message.includes("503")) {
      objectGlob.handleMaintenance()
    }
    if (error.message.includes("401")) {
      let uri = "/authentication/signin/";
      if (window.location.pathname?.includes("authentication/signin")) {
        return Promise.reject(error);
      }
      switch (objectGlob.comanyType) {
        case CompanyType.COLLECTION_AGENCY: {
          uri = "/collector/authentication/signin/"
          break;
        }
        case CompanyType.OWNER_AGENCY: {
          uri = "/owner/authentication/signin/"
          break;
        }
        case CompanyType.BUSINESS: {
          uri = "/business/authentication/signin/"
          break;
        }
        case CompanyType.CORPORATE_HOUSING: {
          uri = "/corporate/authentication/signin/"
          break;
        }
        default:
          break;
      }
      if (objectGlob.oldUri.includes("authentication/signin")) {
        return Promise.reject(error);
      }
      objectGlob.oldUri = uri;
      objectGlob.handleLogout(uri);
    }
    return Promise.reject(error)
  }
)

export function useRequest() {
  const router = useRouter()
  const dispatch = useDispatch()
  const { showDialog, loading } = useGlobal()
  const authorization = useSelector(selectAuthorization)
  const companyType = authorization.user?.company?.type
  objectGlob.comanyType = companyType;
  objectGlob.handleLogout = (uri: string) => {
    showDialog(
      "セッション切れ",
      "お手数ですがもう一度はじめから操作してください",
      "ログイン画面へ"
    ).then(() => {
      Authorization.signOut().then(() => {
        router.push({ pathname: uri })
        objectGlob.oldUri = "";
        loading(false)
      })
    })
  }
  objectGlob.handleMaintenance = () => {
    router.push("/maintenance")
  }
  const reloadToken = (token: string) => {
    const authHeader = { Authorization: `Bearer ${token}` }
    Object.assign(axios.defaults.headers, {
      get: authHeader,
      post: authHeader,
      put: authHeader,
      delete: authHeader,
    })
  }
  reloadToken(authorization?.user?.token || authorization?.guestToken)

  class Request {
    static post<T>(
      url: string,
      params?: Params,
      config = null,
      message?: string | boolean,
      authcheck: boolean = true
    ): Promise<Response<T>> {
      loading(message)

      return new Promise<Response<T>>((resolve, reject) => {
        axios
          .post<Response<T>>(
            url,
            params,
            config || {
              headers: {
                "Content-Type": "application/json",
              },
            }
          )
          .then((response: AxiosResponse) => {
            loading(false)
            resolve(response.data)
          })
          .catch((e) => {
            loading(false)
            if (e.message === "Network Error") {
              showDialog("ネットワークエラー", "リクエストに失敗しました。", "閉じる").then()
            } else if (Axios.isAxiosError(e) && e.response?.status === 403) {
              showDialog("権限エラー", e.response?.data.message, "閉じる").then()
            } else if (Axios.isAxiosError(e) && e.response.status === 500) {
              showDialog("サーバエラー", "リクエストに失敗しました。", "閉じる").then()
            } else if (e.response?.status !== 401 || window.location.pathname?.includes("authentication/signin")) {
              reject(e.response?.data)
            }
          })
      })
    }

    static get<T>(
      url: string,
      params?: Params,
      isSerializer = false,
      message?: string | boolean,
      config?: object
    ): Promise<Response<T>> {
      if (message) {
        loading(message)
      }
      return new Promise<Response<T>>((resolve, reject) => {
        const serializer = isSerializer ? (params: object) => qs.stringify(params) : null
        axios
          .get<Response<T>>(
            url,
            config || {
              headers: {
                "Content-Type": "application/json",
              },
              params,
              paramsSerializer: serializer,
            }
          )
          .then((response: AxiosResponse) => {
            loading(false)
            resolve(response.data)
          })
          .catch((e) => {
            loading(false)
            if (e.message === "Network Error") {
              showDialog("ネットワークエラー", "リクエストに失敗しました。", "閉じる").then()
            } else if (Axios.isAxiosError(e) && e.response?.status === 403) {
              showDialog("権限エラー", e.response?.data.message, "閉じる").then()
            } else if (Axios.isAxiosError(e) && e.response.status === 500) {
              showDialog("サーバエラー", "リクエストに失敗しました。", "閉じる").then()
            } else if (e.response?.status !== 401 || window.location.pathname?.includes("authentication/signin")){
              reject(e.response?.data)
            }
          })
      })
    }

    static put<T>(
      url: string,
      params?: Params,
      config = null,
      message?: string | boolean
    ): Promise<Response<T>> {
      loading(message)
      return new Promise<Response<T>>((resolve, reject) => {
        axios
          .put<Response<T>>(
            url,
            params,
            config || {
              headers: {
                "Content-Type": "application/json",
              },
            }
          )
          .then((response: AxiosResponse) => {
            loading(false)
            resolve(response.data)
          })
          .catch((e) => {
            loading(false)
            if (e.message === "Network Error") {
              showDialog("ネットワークエラー", "リクエストに失敗しました。", "閉じる").then()
            } else if (Axios.isAxiosError(e) && e.response?.status === 403) {
              showDialog("権限エラー", e.response?.data.message, "閉じる").then()
            } else if (Axios.isAxiosError(e) && e.response.status === 500) {
              showDialog("サーバエラー", "リクエストに失敗しました。", "閉じる").then()
            } else if (e.response?.status !== 401 || window.location.pathname?.includes("authentication/signin")) {
              reject(e.response?.data)
            }
          })
      })
    }

    static del(
      url: string,
      params?: Params,
      message?: string | boolean,
      data?: Params
    ): Promise<Response<boolean>> {
      loading(message)
      return new Promise<Response<boolean>>((resolve, reject) => {
        axios
          .delete<Response<boolean>>(url, {
            ...(data ? { data } : {}),
            headers: {
              "Content-Type": "application/json",
            },
            params,
          })
          .then((response: AxiosResponse) => {
            loading(false)
            resolve(response.data)
          })
          .catch((e) => {
            loading(false)
            if (e.message === "Network Error") {
              showDialog("ネットワークエラー", "リクエストに失敗しました。", "閉じる").then()
            } else if (Axios.isAxiosError(e) && e.response?.status === 403) {
              showDialog("権限エラー", e.response?.data.message, "閉じる").then()
            } else if (Axios.isAxiosError(e) && e.response.status === 500) {
              showDialog("サーバエラー", "リクエストに失敗しました。", "閉じる").then()
            } else if (e.response?.status !== 401 || window.location.pathname?.includes("authentication/signin")) {
              reject(e.response?.data)
            }
          })
      })
    }

    static async downloadFile<T>(
      url: string,
      params: Params,
      config: AxiosRequestConfig = {},
      callback?: (percent: number) => void
    ) {
      try {
        const response = await axios.get<T>(url, {
          params,
          headers: { "Content-Type": "application/json" },
          ...config,
          ...(callback && {
            onDownloadProgress: async (e) => {
              callback(Math.round((e.loaded * 80) / e.total))
            },
          })
        })
        return response
      } catch (e) {
        if (e.message === "Network Error") {
          showDialog("ネットワークエラー", "リクエストに失敗しました。", "閉じる").then()
        } else if (Axios.isAxiosError(e) && e.response?.status === 403) {
          showDialog("権限エラー", e.response?.data.message, "閉じる").then()
        } else if (Axios.isAxiosError(e) && e.response.status === 500) {
          showDialog("サーバエラー", "リクエストに失敗しました。", "閉じる").then()
        } else {
          throw new Error("Error")
        }
      }
    }
  }

  class Authorization {
    static signUp(email: string): Promise<boolean> {
      return Request.post<boolean>(API.AUTH_SIGNUP, { email }).then((response) => {
        return response.data
      })
    }

    static signIn(credential: Credential, isBusiness = false): Promise<User> {
      const endpoint = isBusiness ? "-business" : ""
      return Request.post<User>(
        API.AUTH_SIGNIN.replace("{endpoint}", endpoint),
        credential,
        null,
        "ログイン処理をしています、しばらくお待ちください。",
        false
      ).then(async (response) => {
        if (response.data?.id) {
          await dispatch(fetchConfig())
          await dispatch(setUser(response.data))
        }
        return response.data
      })
    }

    static signOut(): Promise<boolean> {
      return new Promise((resolve) => {
        dispatch(clear())
        setTimeout(() => {
          sessionStorage.clear()
          resolve(true)
        }, 500)
      })
    }

    static me(): Promise<User> {
      return Request.get<User>(API.MYPAGE).then((response) => response.data)
    }
  }

  return { Request, Authorization, reloadToken }
}
